import React, { useEffect, useState, useContext } from "react";
import AuthContext from "../context/AuthContext";
import axiosInstance from "../api/axios";
import {
  fetchUserProfile,
  fetchMyInvites,
  generateInviteLink,
  deleteInvite,
  fetchBlockedUsers,
  blockUser,
  unblockUser,
} from "../api/UserAPI";

import SearchBox from "../components/SearchBox";

export default function UserSettings() {
  const { user, token } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [invites, setInvites] = useState([]);
  const [image, setImage] = useState(null);
  const [bio, setBio] = useState(null);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [showUsedInvites, setShowUsedInvites] = useState(true);
  const [blockedUsers, setBlockedUsers] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (token && user) {
      (async () => {
        const profile = await fetchUserProfile(user.username, token);
        setProfile(profile);

        const invites = await fetchMyInvites();
        console.log("INVITES", invites);
        setInvites(invites.results ? invites.results : []);

        const blocks = await fetchBlockedUsers();
        setBlockedUsers(blocks ? blocks : []);
      })();
    }
  }, [token, user]);

  const handleImageChange = (e) => {
    setImage(e.target.files[0]);
  };

  const handleBioChange = (e) => {
    setBio(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();

    if (image) {
      formData.append("profile_pic", image);
    }
    if (bio) {
      formData.append("bio", bio);
    }
    if (!bio && !image) {
      return;
    }

    try {
      const encodedUsername = encodeURIComponent(user.username);
      await axiosInstance.patch(`/profiles/${encodedUsername}/`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Token ${token}`,
        },
      });
      alert("Profile updated!");
      window.location.reload(); // or re-fetch the profile data
    } catch (err) {
      console.error(err);
      alert("Upload failed");
    }
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      // optionally show toast
    } catch (e) {
      console.error("Copy failed", e);
    }
  };

  useEffect(() => {
    if (!copied) return;

    const timer = setTimeout(() => {
      setCopied(false);
    }, 5000);

    return () => clearTimeout(timer);
  }, [copied]);

  const handleCreateInvite = async () => {
    try {
      const newInvite = await generateInviteLink();
      setInvites((prevInvites) => {
        if (!prevInvites) return [newInvite];
        return [newInvite, ...prevInvites]; // prepend newest first
      });
    } catch (e) {
      console.error("Failed to generate invite link", e);
    }
  };

  const handleRevokeInvite = async (id) => {
    try {
      await deleteInvite(id);

      setInvites((prev) => prev.filter((invite) => invite.id !== id));
    } catch (e) {
      console.error("Failed to delete invite", e);
    }
  };

  const handleUnblock = async (id) => {
    try {
      const res = await unblockUser(id);
    } catch (err) {
      console.error("Failed to unblock user", err);
    }
    // get updated blocked list
    try {
      const blocks = await fetchBlockedUsers();
      setBlockedUsers(blocks ? blocks : []);
    } catch (err) {
      console.error("failed to get updated blocked list");
    }
  };

  const handleBlockUser = async (prof) => {
    try {
      const res = await blockUser(prof.user_id);
    } catch (err) {
      console.error("Failed to block user", err);
    }
    // get updated blocked list
    try {
      const blocks = await fetchBlockedUsers();
      setBlockedUsers(blocks ? blocks : []);
    } catch (err) {
      console.error("failed to get updated blocked list");
    }
  };

  if (!profile) {
    return <p>Loading...</p>;
  } else {
    const invitesRemaining = Math.max(0, 2 - invites.length);
    return (
      <div className="flex flex-col max-w-lg mx-auto pb-24 sm:pb-2">
        <h1 className="text-2xl font-bold text-gray-900">@{profile.username}</h1>

        {profile?.role && (
          <span
            className={`mt-2 inline-flex w-fit items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${
              profile.role === "admin" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"
            }`}
          >
            {profile.role}
          </span>
        )}

        <p className="mt-2 text-sm text-gray-500">User Settings</p>
        <div className="flex">
          <img
            src={profile.profile_pic_url}
            alt="Profile Pic"
            className="w-20 h-20 rounded-full object-cover flex-shrink-0 mt-2 border border-black"
          />
          <div className="my-4 pl-9">
            <form onSubmit={handleSubmit} className="flex flex-col space-y-4">
              <h2 className="font-semibold mb-2">Update Profile Picture</h2>
              <input type="file" accept="image/*" onChange={handleImageChange} />
              <h2 className="font-semibold mb-2">Update Bio</h2>
              <textarea
                className="border border-black"
                accept="text"
                placeholder={profile.bio}
                onChange={handleBioChange}
              />
              <button type="submit" className="ml-2 px-4 py-1 bg-blue-600 text-white rounded">
                Submit
              </button>
            </form>
          </div>
        </div>

        {/* Invite Links */}
        <div className="mt-8">
          {copied && <div className="mb-3 rounded bg-green-50 p-3 text-green-700">Copied!</div>}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold">Invite Links</h2>
              {profile?.role == null && (
                <p className="text-sm text-gray-600">
                  {invitesRemaining} of 2 invite{invitesRemaining === 1 ? "" : "s"} remaining.
                </p>
              )}
            </div>

            <button
              onClick={handleCreateInvite}
              disabled={
                creatingInvite ||
                ((invites?.filter((i) => !i.used).length ?? 0) >= 2 && profile?.role === null)
              }
              className="px-3 py-2 rounded bg-black text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {creatingInvite ? "Generating…" : "Generate"}
            </button>
          </div>

          <div className="mt-3 rounded border border-gray-200 bg-white">
            {/* Empty state */}
            {(!invites || invites.length === 0) && (
              <div className="p-4 text-sm text-gray-600">
                No invite links yet. Generate one to share with a friend.
              </div>
            )}

            {/* Active (unused) invites */}
            {invites && invites.length > 0 && (
              <div className="divide-y divide-gray-200">
                {invites
                  .filter((inv) => !inv.used)
                  .map((inv) => (
                    <div key={inv.id} className="p-4 flex items-start justify-between gap-4">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                            Active
                          </span>
                          <span className="text-xs text-gray-500">
                            Created {new Date(inv.created_at).toLocaleString()}
                          </span>
                        </div>

                        <div className="mt-2 font-mono text-sm break-all text-gray-900">
                          {inv.invite_url ?? inv.code}
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-2">
                        <button
                          onClick={() => {
                            handleCopy(inv.invite_url ?? inv.code);
                            setCopied(true);
                          }}
                          className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50"
                        >
                          Copy
                        </button>

                        <button
                          onClick={() => handleRevokeInvite(inv.id)}
                          className="px-3 py-2 rounded border border-red-300 text-red-700 text-sm hover:bg-red-50"
                        >
                          Revoke
                        </button>
                      </div>
                    </div>
                  ))}

                {/* If there are no active invites but some used ones exist */}
                {invites.filter((inv) => !inv.used).length === 0 && (
                  <div className="p-4 text-sm text-gray-600">
                    No active invites right now. Generate a new one to share.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Optional: Used invites section */}
          {invites && invites.some((inv) => inv.used) && (
            <div className="mt-4">
              <button
                onClick={() => setShowUsedInvites((v) => !v)}
                className="text-sm text-gray-700 hover:underline"
              >
                {showUsedInvites
                  ? "Hide used invites"
                  : `Show used invites (${invites.filter((i) => i.used).length})`}
              </button>

              {showUsedInvites && (
                <div className="mt-2 rounded border border-gray-200 bg-white divide-y divide-gray-200">
                  {invites
                    .filter((inv) => inv.used)
                    .map((inv) => (
                      <div key={inv.id} className="p-4 flex items-start justify-between gap-4">
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                              Used
                            </span>
                            <span className="text-xs text-gray-500">
                              Used {inv.used_at ? new Date(inv.used_at).toLocaleString() : "—"}
                            </span>
                          </div>

                          <div className="mt-2 font-mono text-sm break-all text-gray-700">
                            {inv.invite_url ?? inv.code}
                          </div>
                        </div>

                        <button
                          onClick={() => handleCopy(inv.invite_url ?? inv.code)}
                          className="px-3 py-2 rounded border border-gray-300 text-sm hover:bg-gray-50"
                        >
                          Copy
                        </button>
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
        {/* Blocked Users */}
        <div className="mt-6">
          <h2 className="text-lg font-semibold mb-3">Blocked Users</h2>
          <SearchBox onItemClick={handleBlockUser} text={"Block users..."} />

          {blockedUsers.length === 0 ? (
            <p className="text-sm text-gray-500">No blocked users</p>
          ) : (
            <ul className="bg-white border rounded-lg divide-y">
              {blockedUsers.map((blockedUser) => (
                <li key={blockedUser.id} className="flex items-center justify-between p-3">
                  <div className="flex items-center gap-3">
                    <img
                      src={blockedUser.profile_pic_url || `${process.env.PUBLIC_URL}/logo512.png`}
                      alt={blockedUser.user || blockedUser.username}
                      className="h-10 w-10 rounded-full object-cover border"
                    />
                    <div className="font-medium">{blockedUser.user || blockedUser.username}</div>
                  </div>

                  {/* Right side: action */}
                  <button
                    onClick={() => handleUnblock(blockedUser.id)}
                    className="text-sm text-red-500 font-medium hover:text-red-600"
                  >
                    Unblock
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    );
  }
}
